import { Suspense, lazy, useMemo, useState } from 'react';
import { BarChart3, LockKeyhole } from 'lucide-react';
import { FileUploader } from './components/FileUploader';
import { parseWorkbook } from './lib/analytics';
import { WorkbookAnalysis } from './types/analytics';

const Dashboard = lazy(() => import('./components/Dashboard').then((module) => ({ default: module.Dashboard })));

export function App() {
  const [workbook, setWorkbook] = useState<WorkbookAnalysis | null>(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalRows = useMemo(
    () => workbook?.sheets.reduce((total, sheet) => total + sheet.rowCount, 0) ?? 0,
    [workbook],
  );

  const analyseFile = async (file: File) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await parseWorkbook(file);
      setWorkbook(result);
      setActiveSheet(0);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The workbook could not be analysed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="data Insight home">
          <span>DI</span>
          <strong>data Insight</strong>
        </a>
        <p className="topbar-status">Spreadsheet intelligence dashboard</p>
      </header>

      <section className="hero">
        <div className="hero-content">
          <FileUploader onFile={analyseFile} isLoading={isLoading} />
          <aside className="trust-panel" aria-label="Portal capabilities">
            <div>
              <BarChart3 aria-hidden="true" />
              <span>Analysis workspace</span>
            </div>
            <strong>{workbook ? `${totalRows.toLocaleString('en-GB')} rows analysed` : 'Excel, CSV and XLS support'}</strong>
            <p>Upload a workbook, filter by headers, and review clean operational insights in one place.</p>
          </aside>
        </div>
      </section>

      <section className="privacy-strip" aria-label="Privacy notice">
        <LockKeyhole size={16} aria-hidden="true" />
        <strong>Your data is not saved anywhere.</strong>
        <span>Files are analysed locally in your browser session and are not uploaded, stored, logged, or shared by this portal.</span>
      </section>

      {error && <p className="error-banner">{error}</p>}

      {workbook ? (
        <Suspense fallback={<p className="loading-panel">Preparing dashboard...</p>}>
          <Dashboard
            key={`${workbook.fileName}-${activeSheet}`}
            workbook={workbook}
            activeSheet={activeSheet}
            onSheetChange={setActiveSheet}
          />
        </Suspense>
      ) : (
        <section className="starter-grid" aria-label="Feature overview">
          {[
            ['Auto profiling', 'Detect column types, missing values, duplicates, and data completeness.'],
            ['Smart grouping', 'Find repeated categories and common factors that explain the dataset.'],
            ['Private by design', 'No bundled workbook, no server upload, and no saved user data.'],
          ].map(([title, copy]) => (
            <article className="starter-card" key={title}>
              <span />
              <h2>{title}</h2>
              <p>{copy}</p>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
